import React, { Fragment, useState } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { Report, Replay } from '@material-ui/icons';
import { FaFileInvoice } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import {
  Chip,
  Box,
  Card,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  CircularProgress,
  makeStyles,
  Tooltip
} from '@material-ui/core';
import { req } from 'src/services/api';
import ModalFaturamento from './ModalFaturamento';
import { toast } from 'react-toastify';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';
import {
  ORDER_STATUS,
  getOrderStatusLabel,
  getOrderStatusColor,
  normalizeOrderStatus
} from 'src/constants/orderStatus';
import {
  canAttemptOrderRefund,
  isOrderRefundBlockedByPayment
} from 'src/constants/refundStatus';

const useStyles = makeStyles(theme => ({
  root: {
    '& .MuiSvgIcon-root': {
      color: '#F18750',
      opacity: 0.8
    },
    '& .MuiSvgIcon-root:hover ': {
      //
      color: '#ff5c08',
      opacity: 1
    }
  },
  avatar: {
    marginRight: theme.spacing(2)
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10
  },
  actionIcon: {
    padding: 4
  }
}));

const formas_de_pagamento = {
  2: 'CARTÃO DE CRÉDITO',
  1: 'PIX'
};

const Results = ({
  className,
  total,
  orders,
  loading,
  isFinished,
  limit,
  handleLimitChange,
  page,
  handlePageChange,
  permissions,
  getOrders,
  ...rest
}) => {
  const classes = useStyles();
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [pedidoSelecionado, setPedidoSelecionado] = useState(null);
  const [retryingRefundOrderUuid, setRetryingRefundOrderUuid] = useState(null);

  const navigate = useNavigate();

  const visualizeOrder = async uuid => {
    try {
      const resp = await req(
        `
          mutation{
            visualize_order(uuid: "${uuid}")
          }
          `
      );
      if (resp.data) {
        console.log('visualizado');
      } else if (resp.errors) {
        console.log(resp.error);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const hasNewOrders = orders.find(order => {
    const status = normalizeOrderStatus(order.status);
    return !order.visualisado && status <= ORDER_STATUS.PAYMENT_CONFIRMED;
  });

  function faturarPedido(e, order) {
    e.stopPropagation();
    setPedidoSelecionado(order);
  }

  async function executeRetryRefund(order) {
    setRetryingRefundOrderUuid(order.uuid);
    try {
      const resp = await req(
        `
          mutation{
            retry_order_refund(uuid: "${order.uuid}")
          }
        `
      );

      if (resp.data) {
        toast.success(
          resp.data.retry_order_refund || 'Estorno realizado com sucesso!'
        );
        getOrders();
      } else if (resp.errors) {
        toast.error(resp.errors[0].message);
        getOrders();
      }
    } catch (err) {
      toast.error('Não foi possível tentar o estorno novamente.');
    } finally {
      setRetryingRefundOrderUuid(null);
    }
  }

  function retryRefund(e, order) {
    e.stopPropagation();

    const hasAttempts = (order.refundAttempts ?? 0) > 0;

    confirmAlert({
      title: 'Confirmar estorno',
      message: hasAttempts
        ? 'Deseja tentar o estorno deste pedido novamente?'
        : 'Deseja tentar o estorno deste pedido?',
      buttons: [
        {
          label: 'Sim',
          onClick: () => executeRetryRefund(order)
        },
        {
          label: 'Não'
        }
      ]
    });
  }

  const navigateToDetails = async order => {
    await visualizeOrder(order.uuid);
    navigate('/pedido', { state: { order, permissions } });
  };

  return (
    <Fragment>
      <Card className={clsx(classes.root, className)} {...rest}>
        <PerfectScrollbar>
          <Box minWidth={1050}>
            <Table>
              <TableHead>
                <TableRow>
                  {!isFinished && hasNewOrders && (
                    <TableCell align="center" colSpan={1}>
                      {' '}
                    </TableCell>
                  )}
                  <TableCell align="center">Código do Pedido</TableCell>
                  <TableCell align="center">Cliente</TableCell>
                  <TableCell align="center">Valor da Compra</TableCell>
                  <TableCell align="center">Bairro</TableCell>
                  <TableCell align="center">Zona</TableCell>
                  <TableCell align="center">Horario da Compra</TableCell>
                  <TableCell align="center">Forma de Pagamento</TableCell>
                  <TableCell align="center">Status do Pedido</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow hover key="key">
                    <TableCell colSpan={9} align="center">
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          justifyContent: 'center'
                        }}
                      >
                        <CircularProgress color="primary" size={20} />
                        <Typography style={{ marginLeft: 10 }}>
                          Carregando...
                        </Typography>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : orders.length > 0 ? (
                  orders.map(order => (
                    <TableRow
                      hover
                      key={order.uuid}
                      onClick={() => navigateToDetails(order)}
                      selected={selectedOrderIds.indexOf(order.uuid) !== -1}
                      style={{
                        cursor: 'pointer'
                      }}
                    >
                      {!isFinished && !order.visualisado && (
                        <TableCell align="center">
                          <Chip
                            style={{
                              backgroundColor: '#3BB143',
                              color: 'white'
                            }}
                            label="Novo"
                            size="small"
                          />
                        </TableCell>
                      )}

                      <TableCell align="center">
                        <strong>
                          {order.uuid.slice(30, 36).toUpperCase()}
                        </strong>
                      </TableCell>

                      <TableCell>{order.user.nome}</TableCell>

                      <TableCell align="center">
                        {order.valor_total.toLocaleString('pt-br', {
                          style: 'currency',
                          currency: 'BRL'
                        })}
                      </TableCell>

                      <TableCell align="center">
                        {order.deliveryType == 2
                          ? 'Retirada na loja'
                          : order.bairro}
                      </TableCell>
                      <TableCell align="center">
                        {order.deliveryType == 2 ? '-' : order.zona}
                      </TableCell>
                      <TableCell align="center">
                        {new Date(
                          parseInt(order.data_pedido, 10)
                        ).toLocaleString('pt-BR')}
                      </TableCell>

                      <TableCell align="center">
                        {order.paymentMethod
                          ? formas_de_pagamento[order.paymentMethod.type] ?? '-'
                          : '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          style={{
                            backgroundColor: getOrderStatusColor(order.status),
                            color: 'white',
                            maxWidth: '100%'
                          }}
                          label={getOrderStatusLabel(order.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <div className={classes.row}>
                          {![
                            ORDER_STATUS.PENDING_PAYMENT,
                            ORDER_STATUS.CANCELLED
                          ].includes(normalizeOrderStatus(order.status)) &&
                            !order.faturado && (
                              <FaFileInvoice
                                onClick={e => faturarPedido(e, order)}
                                style={{ color: '#222', fontSize: 18 }}
                              />
                            )}
                          {(canAttemptOrderRefund(order) ||
                            isOrderRefundBlockedByPayment(order)) && (
                            <Tooltip
                              title={
                                isOrderRefundBlockedByPayment(order)
                                  ? 'O pedido não foi pago, então não pode ser solicitado um reembolso.'
                                  : (order.refundAttempts ?? 0) > 0
                                  ? 'Tentar estorno novamente'
                                  : 'Tentar estorno'
                              }
                            >
                              <span>
                                <IconButton
                                  className={classes.actionIcon}
                                  disabled={
                                    retryingRefundOrderUuid === order.uuid ||
                                    isOrderRefundBlockedByPayment(order)
                                  }
                                  onClick={e => retryRefund(e, order)}
                                  size="small"
                                >
                                  {retryingRefundOrderUuid === order.uuid ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <Replay style={{ fontSize: 18 }} />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          {order.status_interno !== '' && (
                            <Report style={{ color: '#E83B3C' }} />
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow hover key="key">
                    <TableCell colSpan={9} align="center">
                      Nenhum pedido encontrado
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </PerfectScrollbar>
        <TablePagination
          component="div"
          count={total}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={handleLimitChange}
          page={page}
          rowsPerPage={limit}
          rowsPerPageOptions={[15, 25, 50, 100]}
        />
      </Card>
      <ModalFaturamento
        visible={pedidoSelecionado !== null}
        pedido={pedidoSelecionado}
        atualizarLista={getOrders}
        onClose={() => setPedidoSelecionado(null)}
      />
    </Fragment>
  );
};

Results.propTypes = {
  className: PropTypes.string,
  orders: PropTypes.array.isRequired
};

export default Results;
